export const MEMBERSHIP_PLAN_CAPACITIES = [1, 5, 10, 30] as const;

export type MembershipPlanCapacity =
  (typeof MEMBERSHIP_PLAN_CAPACITIES)[number];

export type MembershipInviteType =
  | 'FAMILY_PROFILE'
  | 'SPONSORED_SEAT';

export type MembershipSeatAuthorityStatus =
  | 'HEALTHY'
  | 'FULL'
  | 'INVALID';

export interface MembershipSeatAuthorityInput {
  planCapacity: MembershipPlanCapacity;
  activeSponsoredSeats?: number;
  reservedSponsoredSeats?: number;
  returnedSeatCount?: number;
  familyInvitesSent?: number;
  familyInvitesPending?: number;
}

export interface MembershipSeatAuthorityReport {
  generatedAt: string;
  status: MembershipSeatAuthorityStatus;

  planCapacity: MembershipPlanCapacity;

  ownerSeatCount: 1;
  activeSponsoredSeatCount: number;
  reservedSponsoredSeatCount: number;

  occupiedSeatCount: number;
  availableSeatCount: number;
  returnedSeatCount: number;

  familyInvitesSent: number;
  familyInvitesPending: number;
  familyInvitesUnlimited: true;

  sponsoredInviteReservationDays: 7;
  canReserveSponsoredSeat: boolean;

  summary: string;
}

export interface MembershipInviteDecision {
  inviteType: MembershipInviteType;
  allowed: boolean;
  reservesSeat: boolean;
  reservationDays: 0 | 7;
  reason:
    | 'FAMILY_INVITES_UNLIMITED'
    | 'SPONSORED_SEAT_AVAILABLE'
    | 'SPONSORED_SEAT_CAPACITY_FULL'
    | 'INVALID_MEMBERSHIP_STATE';
}

const OWNER_SEAT_COUNT = 1 as const;
const SPONSORED_INVITE_RESERVATION_DAYS = 7 as const;

function isNonNegativeInteger(value: number): boolean {
  return Number.isInteger(value) && value >= 0;
}

function isAllowedCapacity(
  value: number,
): value is MembershipPlanCapacity {
  return MEMBERSHIP_PLAN_CAPACITIES.includes(
    value as MembershipPlanCapacity,
  );
}

export function projectMembershipSeatAuthority(
  input: MembershipSeatAuthorityInput,
): MembershipSeatAuthorityReport {
  const activeSponsoredSeatCount =
    input.activeSponsoredSeats ?? 0;

  const reservedSponsoredSeatCount =
    input.reservedSponsoredSeats ?? 0;

  const returnedSeatCount =
    input.returnedSeatCount ?? 0;

  const familyInvitesSent =
    input.familyInvitesSent ?? 0;

  const familyInvitesPending =
    input.familyInvitesPending ?? 0;

  const numericValues = [
    activeSponsoredSeatCount,
    reservedSponsoredSeatCount,
    returnedSeatCount,
    familyInvitesSent,
    familyInvitesPending,
  ];

  const valuesValid =
    isAllowedCapacity(input.planCapacity) &&
    numericValues.every(isNonNegativeInteger);

  const occupiedSeatCount =
    OWNER_SEAT_COUNT +
    activeSponsoredSeatCount +
    reservedSponsoredSeatCount;

  const withinCapacity =
    occupiedSeatCount <= input.planCapacity;

  if (!valuesValid || !withinCapacity) {
    return Object.freeze({
      generatedAt: new Date().toISOString(),
      status: 'INVALID',
      planCapacity: input.planCapacity,
      ownerSeatCount: OWNER_SEAT_COUNT,
      activeSponsoredSeatCount,
      reservedSponsoredSeatCount,
      occupiedSeatCount,
      availableSeatCount: 0,
      returnedSeatCount,
      familyInvitesSent,
      familyInvitesPending,
      familyInvitesUnlimited: true,
      sponsoredInviteReservationDays:
        SPONSORED_INVITE_RESERVATION_DAYS,
      canReserveSponsoredSeat: false,
      summary:
        'Invalid membership seat state: occupied seats exceed capacity or counters are invalid.',
    });
  }

  const availableSeatCount =
    input.planCapacity - occupiedSeatCount;

  const status: MembershipSeatAuthorityStatus =
    availableSeatCount === 0 ? 'FULL' : 'HEALTHY';

  return Object.freeze({
    generatedAt: new Date().toISOString(),
    status,
    planCapacity: input.planCapacity,
    ownerSeatCount: OWNER_SEAT_COUNT,
    activeSponsoredSeatCount,
    reservedSponsoredSeatCount,
    occupiedSeatCount,
    availableSeatCount,
    returnedSeatCount,
    familyInvitesSent,
    familyInvitesPending,
    familyInvitesUnlimited: true,
    sponsoredInviteReservationDays:
      SPONSORED_INVITE_RESERVATION_DAYS,
    canReserveSponsoredSeat: availableSeatCount > 0,
    summary:
      `Capacity=${input.planCapacity} | ` +
      `Owner=1 | ` +
      `Active=${activeSponsoredSeatCount} | ` +
      `Reserved=${reservedSponsoredSeatCount} | ` +
      `Available=${availableSeatCount} | ` +
      `Returned=${returnedSeatCount}`,
  });
}

export function decideMembershipInvite(
  report: MembershipSeatAuthorityReport,
  inviteType: MembershipInviteType,
): MembershipInviteDecision {
  if (report.status === 'INVALID') {
    return Object.freeze({
      inviteType,
      allowed: false,
      reservesSeat: false,
      reservationDays: 0,
      reason: 'INVALID_MEMBERSHIP_STATE',
    });
  }

  if (inviteType === 'FAMILY_PROFILE') {
    return Object.freeze({
      inviteType,
      allowed: true,
      reservesSeat: false,
      reservationDays: 0,
      reason: 'FAMILY_INVITES_UNLIMITED',
    });
  }

  if (report.canReserveSponsoredSeat) {
    return Object.freeze({
      inviteType,
      allowed: true,
      reservesSeat: true,
      reservationDays: 7,
      reason: 'SPONSORED_SEAT_AVAILABLE',
    });
  }

  return Object.freeze({
    inviteType,
    allowed: false,
    reservesSeat: false,
    reservationDays: 0,
    reason: 'SPONSORED_SEAT_CAPACITY_FULL',
  });
}

export const MEMBERSHIP_SEAT_AUTHORITY_READY = true;
